import Link from 'next/link';

import { Icon } from '@/components/Icon/Icon';
import { getUiConfig } from '@/config/client/getUiConfig';

interface FooterProps {
  links: {
    foxLinks: {
      title: string;
      href: string;
    }[];
    foxNetworks: {
      title: string;
      href: string;
    }[];
  };
  className?: string;
  isBright?: boolean;
}

export function Footer(props: FooterProps): React.ReactNode {
  const { links, className = '' } = props;
  const uiConfig = getUiConfig();

  const currentYear = new Date().getFullYear();

  return (
    <footer className={`w-full bg-base-black pb-4 pt-[40px] md:pb-[30px] lg:pt-[50px] ${className} `}>
      <div className="container mb-[140px] flex max-w-coreMaxWidth flex-col items-start justify-between md:items-center lg:mx-auto lg:mb-[80px] lg:max-w-coreMaxWidth lg:flex-row lg:items-start xlmid:px-[184px]">
        <div className="mx-auto inline-flex w-full flex-col items-center border-b border-b-ebonyClay pb-6 text-white lg:mx-0 lg:w-1/2 lg:items-start lg:border-0">
          <Icon alt="FOX One Logo" className="mb-4 size-24 sm:hidden" id="fox-one-logo-portrait" />
          <div className="flex">
            <Icon alt="FOX One Logo" className="hidden size-24 sm:mr-5 sm:block" id="fox-one-logo-portrait" />
            <div className="mt-0 sm:mt-3 sm:w-[318px] md:w-[322px]">
              <h3 className="mb-3 font-FoxOneAeroBold text-[20px]/[21px] capitalize text-white md:text-[25px] md:font-bold">
                Download the Mobile App
              </h3>
              <div className="flex w-full gap-5 xs:justify-center sm:justify-start">
                <Link href={uiConfig.downloadMobileAppLink}>
                  <Icon alt="Apple Store Download Logo" className="h-9 w-[106px]" id="app-store-download" />
                </Link>
                <Link href={uiConfig.downloadMobileAppLink}>
                  <Icon alt="Google Store Download Logo" className="h-9 w-[120px]" id="google-app-store-download" />
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col py-[12px] sm:flex-row sm:flex-wrap md:pl-[180px] md:pr-[96px] lg:w-1/2 lg:px-0">
          <div className="mb-4 mt-6 w-full pl-[20.6px] sm:w-1/2 md:mt-0">
            <h2 className="mb-[8px] font-FoxOneAero text-[19px] font-medium text-white">FOX Links</h2>
            <ul className="grid w-full auto-cols-fr grid-flow-col grid-rows-5 gap-x-2.5 gap-y-1">
              {links.foxLinks.map((link) => (
                <li key={link.title} className="h-[17.5px] w-fit">
                  <a
                    data-analytics={
                      link.title ? `footer-${link.title.toLowerCase().split(' ').join('-')}-link` : 'footer-link'
                    }
                    className="flex h-full items-center justify-center font-FoxOneAero text-[12px] font-medium text-ironGray"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="mb-[40px] mt-6 w-full pl-[20.6px] sm:w-1/2 md:mt-0">
            <h2 className="mb-[8px] font-FoxOneAero text-[19px] font-medium text-white">FOX Networks</h2>
            <ul className="grid w-full auto-cols-fr grid-flow-col grid-rows-4 gap-x-2.5 gap-y-1">
              {links.foxNetworks.map((link) => (
                <li key={link.title} className="h-[17.5px] w-fit">
                  <a
                    data-analytics={
                      link.title ? `footer-${link.title.toLowerCase().split(' ').join('-')}-link` : 'footer-link'
                    }
                    className="flex h-full items-center justify-center font-FoxOneAero text-[12px] font-medium text-ironGray"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <span className="w-full pl-[20.6px] text-[13px]/[18px] font-light text-ironGray">
            &trade; and &copy; {currentYear} Fox Media LLC
          </span>
        </div>
      </div>
    </footer>
  );
}
